import { catchError, map } from 'rxjs/operators';
import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MessageService } from '../../../core/service/message.service';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { Tenant } from '../../model/tenant';
import { Observable, of } from 'rxjs';
import { LeaseAgreementCategory } from '../../model/const/enums';
import { AutoCompleteSelectionItem, IAutoCompleteService } from '../../../core/component/autocomplete-remote/autocomplete-remote.component';

@Injectable()
export class TenantService implements IAutoCompleteService {
  constructor(
    private _client: HttpClient,
    private _messageService: MessageService,
    @Inject(APP_CONFIG) config: IAppConfig
  ) {
    this._endpoint = `${config.apiEndpointV2}/tenant`;
  }

  getSelectionItem(id: string): Observable<AutoCompleteSelectionItem> {
    return this._client.get<Tenant>(`${this._endpoint}/selectionItem/${id}`).pipe(
      map(data => this.createAutoCompleteSelectionItem(data)),
      catchError(e => {
        this._messageService.showError(e);
        return [];
      })
    );
  }

  getSelection(filter: string): Observable<AutoCompleteSelectionItem[]> {
    return this._client.post<Tenant[]>(`${this._endpoint}/selection`, { filter: filter, maxItems: 20 }).pipe(
      map(data => data.map(item => this.createAutoCompleteSelectionItem(item))),
      catchError(e => {
        this._messageService.showError(e);
        return [];
      })
    );
  }

  private createAutoCompleteSelectionItem(tenant: Tenant): AutoCompleteSelectionItem {
    if (!tenant) {
      return new AutoCompleteSelectionItem('', null, null);
    }
    let shortName = `${tenant.contactNumber} ${tenant.name}`.trim();
    let name =
      `${shortName}${tenant.contactIdentification ? ' (' + tenant.contactIdentification + ')' : ''} ${tenant.phone ? ', ' + tenant.phone : ''}`.trim();
    return new AutoCompleteSelectionItem(name, tenant.id, Object.assign(new Tenant(), tenant), shortName);
  }

  private readonly _endpoint: string;

  getById(id: string): Observable<Tenant> {
    return this._client.get<Tenant>(`${this._endpoint}/id/${id}`).pipe(map(val => Object.assign(new Tenant(), val)));
  }

  getByRentalObject(rentalObjectId: string): Observable<Tenant[]> {
    return this._client.get(`${this._endpoint}/filter/rental-object/${rentalObjectId}`).pipe(
      map(list => {
        if (Array.isArray(list)) {
          const result = list.map(x => {
            return Object.assign(new Tenant(x.category), x);
          });
          return result;
        }
        return [];
      })
    );
  }

  getByCompany(companyId: string): Observable<Tenant[]> {
    return this._client.get(`${this._endpoint}/filter/company/${companyId}`).pipe(
      map(list => {
        if (Array.isArray(list)) {
          const result = list.map(x => {
            return Object.assign(new Tenant(x.category), x);
          });
          return result;
        }
        return [];
      })
    );
  }
}
